import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {Col, FormFeedback, FormGroup, Input, Label, Row} from 'reactstrap';
import {SelectField} from 'src/components/react-select';
import Button from 'src/components/button';
import DataTable from 'src/components/dataTable';
import {BillingBlockRun} from 'src/core/data/api/BillingBlock';
import {useBlockRunDispatches, useBlockRuns} from './queries';
import {Period, useBillingBlock} from './provider';
import {ListState, RefreshButton, SubtabHeader, Truncated} from './list-state';
import StatusBadge, {useEnumLabel} from './status-badge';
import {defaultPeriod, filterRuns, PERIOD_PRESETS, PeriodPreset, presetPeriod, validatePeriod} from './period';
import {formatDateTime, orNotAvailable} from './format';
import styles from './billing-block.module.scss';

const noop = () => undefined;

const Dispatches = ({run, onBack}: {run: BillingBlockRun; onBack: () => void}) => {
  const intl = useIntl();
  const enumLabel = useEnumLabel();
  const query = useBlockRunDispatches(run.id);
  const rows = (query.data || []).map((dispatch) => ({
    ...dispatch,
    deviceLabel: orNotAvailable(intl, dispatch.deviceName),
    customerLabel: orNotAvailable(intl, dispatch.customerName),
    typeLabel: enumLabel('DispatchType', dispatch.type),
    resultLabel: enumLabel('DispatchResult', dispatch.result),
    createdAtLabel: formatDateTime(intl, dispatch.createdAt),
    // The server's failure detail is shown as sent, untranslated (FR-018).
    detailLabel: dispatch.detail || '',
  }));

  return (
    <section className={styles.section}>
      <SubtabHeader title={intl.formatMessage({id: 'billingBlockRunTitle'}, {date: formatDateTime(intl, run.startedAt)})} query={query}>
        <Button className="neutral" color="secondary" size="sm" onClick={onBack}>
          {intl.formatMessage({id: 'billingBlockBackToRuns'})}
        </Button>
      </SubtabHeader>
      <ListState query={query} rows={rows} emptyMessage={intl.formatMessage({id: 'billingBlockEmptyDispatches'})}>
        {(list) => (
          <DataTable
            add={noop}
            edit={noop}
            remove={noop}
            showGroupButtons={false}
            keyField="id"
            list={list}
            headers={[
              {title: 'billingBlockColumnDevice', field: 'deviceLabel', dataSort: true, dataFormat: (cell: string) => <Truncated text={cell} />},
              {title: 'billingBlockColumnCustomer', field: 'customerLabel', dataSort: true, dataFormat: (cell: string) => <Truncated text={cell} />},
              {title: 'billingBlockColumnCharge', field: 'billingChargeId'},
              {title: 'billingBlockColumnType', field: 'typeLabel', dataFormat: (cell: string, row: typeof list[number]) => <StatusBadge kind="DispatchType" value={row.type} />},
              {title: 'billingBlockColumnResult', field: 'resultLabel', dataFormat: (cell: string, row: typeof list[number]) => <StatusBadge kind="DispatchResult" value={row.result} />},
              {title: 'billingBlockColumnTime', field: 'createdAtLabel'},
              {title: 'billingBlockColumnDetail', field: 'detailLabel'},
            ]}
          />
        )}
      </ListState>
    </section>
  );
};

const ReportTab = () => {
  const intl = useIntl();
  const enumLabel = useEnumLabel();
  const {period, setPeriod} = useBillingBlock();
  const query = useBlockRuns();
  const runs = query.data || [];
  const effective = period || defaultPeriod(runs);
  // Null until the user edits a date: the fields show the period in effect.
  const [draft, setDraft] = useState<Period | null>(null);
  const [selected, setSelected] = useState<BillingBlockRun | null>(null);
  const [openRun, setOpenRun] = useState<BillingBlockRun | null>(null);
  const fields = draft || effective || {from: '', to: ''};

  if (openRun) {
    return (
      <Dispatches
        run={openRun}
        onBack={() => {
          setOpenRun(null);
          setSelected(null);
        }}
      />
    );
  }

  const CUSTOM = 'custom';
  const preset = draft ? CUSTOM : effective?.preset || CUSTOM;
  const presetOptions = [
    {id: CUSTOM, title: intl.formatMessage({id: 'reportCustomPeriod'})},
    ...PERIOD_PRESETS.map(({id, labelId}) => ({id, title: intl.formatMessage({id: labelId})})),
  ];
  const choosePreset = (id: string) => {
    setSelected(null);
    setDraft(null);
    // Custom keeps the dates in effect, now editable.
    setPeriod(id === CUSTOM ? (effective && {from: effective.from, to: effective.to}) : presetPeriod(id as PeriodPreset));
  };

  const invalid = draft ? validatePeriod(draft) : null;
  const errorId = 'billing-block-period-error';
  const shown = (effective ? filterRuns(runs, effective) : []).map((run) => ({
    ...run,
    startedAtLabel: formatDateTime(intl, run.startedAt),
    finishedAtLabel: formatDateTime(intl, run.finishedAt),
    statusLabel: enumLabel('RunStatus', run.status),
  }));
  const dateField = (name: 'from' | 'to', labelId: string) => (
    <FormGroup className="custom-formGroup">
      <Label for={`billing-block-period-${name}`}>{intl.formatMessage({id: labelId})}</Label>
      <Input
        id={`billing-block-period-${name}`}
        name={name}
        type="date"
        value={fields[name]}
        invalid={!!invalid}
        aria-describedby={invalid ? errorId : undefined}
        onChange={(event) => setDraft({...fields, [name]: event.target.value})}
      />
      {name === 'to' && invalid && <FormFeedback id={errorId}>{intl.formatMessage({id: invalid})}</FormFeedback>}
    </FormGroup>
  );

  const actions = (
    <>
      <RefreshButton query={query} />
      <Button className="solid" color="theme" size="sm" disabled={!selected} onClick={() => setOpenRun(selected)}>
        {intl.formatMessage({id: 'billingBlockOpenRun'})}
      </Button>
    </>
  );

  return (
    <section className={styles.section}>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (draft && !invalid) {
            setSelected(null);
            setPeriod(draft);
            setDraft(null);
          }
        }}
      >
        <Row form>
          <Col md={3}>
            <FormGroup className="custom-formGroup">
              <Label for="billing-block-period-preset">{intl.formatMessage({id: 'reportPeriod'})}</Label>
              <SelectField
                id="billing-block-period-preset"
                label={intl.formatMessage({id: 'reportPeriod'})}
                value={preset}
                options={presetOptions}
                onChange={choosePreset}
              />
            </FormGroup>
          </Col>
          {preset === CUSTOM && (
            <>
              <Col md={3}>{dateField('from', 'billingBlockPeriodFrom')}</Col>
              <Col md={3}>{dateField('to', 'billingBlockPeriodTo')}</Col>
              <Col md={3} className="d-flex align-items-end pb-2">
                <Button className="neutral" color="secondary" size="sm" type="submit" disabled={!draft || !!invalid}>
                  {intl.formatMessage({id: 'billingBlockApplyPeriod'})}
                </Button>
              </Col>
            </>
          )}
        </Row>
      </form>
      <ListState
        query={query}
        rows={shown}
        emptyMessage={intl.formatMessage({id: runs.length === 0 ? 'billingBlockEmptyRunsAtAll' : 'billingBlockEmptyRuns'})}
        actions={actions}
        emptyAction={runs.length === 0 ? undefined : {label: intl.formatMessage({id: 'billingBlockResetPeriod'}), onClick: () => {
          setDraft(null);
          setPeriod(null);
        }}}
      >
        {(list) => (
          <DataTable
            key={`${query.dataUpdatedAt}-${effective?.from}-${effective?.to}`}
            add={noop}
            edit={noop}
            remove={noop}
            showGroupButtons={false}
            keyField="id"
            list={list}
            onSelectRow={setSelected}
            extraHeaderEl={() => actions}
            headers={[
              {title: 'billingBlockColumnStartedAt', field: 'startedAtLabel'},
              {title: 'billingBlockColumnFinishedAt', field: 'finishedAtLabel'},
              {title: 'billingBlockColumnStatus', field: 'statusLabel', dataSort: true, dataFormat: (cell: string, row: BillingBlockRun) => <StatusBadge kind="RunStatus" value={row.status} />},
              {title: 'billingBlockColumnDispatchCount', field: 'dispatchCount', dataSort: true},
            ]}
          />
        )}
      </ListState>
    </section>
  );
};

export default ReportTab;
